import React, { useState } from "react";
import { apiPost } from "../../api";
import { toast } from "react-toastify";
import { Megaphone, Loader2 } from "lucide-react";

const AnnouncementPage = () => {
  const [form, setForm] = useState({
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await apiPost("/api/admin/general/announcement", {
        subject: form.subject,
        message: form.message,
      });

      if (response.data.success) {
        toast.success(response.data.message || "Announcement sent successfully!");
        setForm({ subject: "", message: "" });
      } else {
        toast.error(response.data.message || "Failed to send announcement");
      }
    } catch (error) {
      console.error("Error sending announcement:", error);
      if (error.response && error.response.data && error.response.data.message) {
        toast.error(`Failed to send announcement: ${error.response.data.message}`);
      } else {
        toast.error("Failed to send announcement. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="text-foreground p-4 h-full overflow-hidden">
      <div className="max-w-4xl mx-auto h-full flex flex-col">
        {/* Header */}
        <div className="flex-shrink-0">
          <div className="flex items-center gap-3 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
            <span className="h-px w-6 bg-ginox-green" aria-hidden="true" />
            Community
          </div>
          <h1 className="font-serif italic text-[45px] leading-[1.02] text-foreground">Send <span className="text-gradient-ginox">Announcement</span></h1>
        </div>
        <p className="text-sm text-muted-foreground mb-6 flex-shrink-0">
          Broadcast a message to all users on the platform.
        </p>

        {/* Form Container */}
        <div className="rounded-lg border border-border/70 bg-card flex-1 flex flex-col min-h-0">
          <form onSubmit={handleSubmit} className="p-5 flex flex-col h-full">
            {/* Subject Field */}
            <div className="mb-4 flex-shrink-0">
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5">
                Subject *
              </label>
              <input
                type="text"
                name="subject"
                value={form.subject}
                onChange={handleChange}
                placeholder="Enter announcement subject..."
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30"
                required
                disabled={loading}
              />
            </div>

            {/* Message Field */}
            <div className="flex-1 flex flex-col min-h-0">
              <label className="block text-xs font-medium uppercase tracking-[0.06em] text-muted-foreground mb-1.5 flex-shrink-0">
                Message *
              </label>
              <textarea
                name="message"
                value={form.message}
                onChange={handleChange}
                placeholder="Enter your announcement message here..."
                className="w-full rounded-md border border-border/70 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 focus:border-ginox-green/50 focus:outline-none focus:ring-1 focus:ring-ginox-green/30 resize-none flex-1"
                required
                disabled={loading}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end mt-4 flex-shrink-0">
              <button
                type="submit"
                disabled={loading}
                className="rounded-md border border-ginox-green/60 bg-ginox-green/10 px-4 py-2 text-xs font-medium text-ginox-green hover:bg-ginox-green/20 transition-colors disabled:opacity-50 flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Megaphone className="w-3.5 h-3.5" />
                    Send Announcement
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AnnouncementPage;
